"use client";

import * as React from "react";
import {
  useWalletPortfolioContext,
  usePortfolioFiat,
} from "../../providers/wallet-portfolio-provider";
import { useTokenInfo } from "../../queries/use-token-info";
import { useWithdrawPositions } from "../../queries/use-withdraw-positions";
import { APP_LAUNCHER_ICONS_BASE, POINTS_WEB } from "../../constants";
import {
  formatPortfolioTokenAmount,
  formatPortfolioUsd,
  isPointsWithdrawPosition,
} from "../../utils";
import { SectionCard } from "./SectionCard";
import { WithdrawDialog, type WithdrawDialogPosition } from "./WithdrawDialog";
import { useWithdrawFlow } from "./useWithdrawFlow";
import { ExternalLinkIcon } from "../icons";
import { handleOutlinkClick } from "../../../../lib/outlink";

const SECTION_TITLE = "ONEpoints";

/**
 * cross-points My-Point의 예치 퀘스트(게임 토큰 예치) 포지션 섹션.
 * 게임 토큰 예치 잔액을 나열하고, Withdraw는 외부 이동 없이 내장
 * `WithdrawDialog`(토큰 리스트 + 금액 입력)로 처리한다.
 * `sendTransaction` 미주입 환경에서는 Points 웹으로 아웃링크 폴백.
 */
export function PointsSection() {
  const { env, walletAddress, onOutlink } = useWalletPortfolioContext();
  const fiat = usePortfolioFiat();
  const hasAddress = !!walletAddress;
  const { getTokenImage, getNativeToken } = useTokenInfo(env, hasAddress);
  const flow = useWithdrawFlow("points");
  const { positions, isLoading: positionsLoading } = useWithdrawPositions(
    env,
    walletAddress,
    hasAddress,
  );

  // ONErewards에 잡힌 활성 풀은 제외한다. pools API에 없는 quest 풀만
  // useWithdrawPositions에서 `Unknown`으로 분류되어 ONEpoints에 노출된다.
  const pointPositions = React.useMemo<WithdrawDialogPosition[]>(
    () =>
      positions
        .filter(isPointsWithdrawPosition)
        .map((p) => ({
          ...p,
          icon: getTokenImage(p.tokenAddress, p.tokenSymbol),
        })),
    [positions, getTokenImage],
  );

  const sectionIcon = `${APP_LAUNCHER_ICONS_BASE[env]}/point.png`;
  const pointsUrl = POINTS_WEB[env];

  return (
    <>
      <SectionCard
        sectionKey="points"
        title={SECTION_TITLE}
        icon={sectionIcon}
        isEmpty={pointPositions.length === 0}
        loading={positionsLoading}
      >
        {pointPositions.map((position) => {
          const depositedUsd = position.price
            ? formatPortfolioUsd(
                position.depositedRaw,
                position.decimals,
                position.price,
                fiat,
              )
            : null;

          return (
            <article
              className="wp-item"
              data-side="reward"
              key={position.poolAddress}
            >
              <div className="wp-item-body">
                <div className="wp-item-line">
                  <span className="wp-item-label">Token</span>
                  <div className="wp-svc-token">
                    {position.icon && (
                      <img
                        className="wp-svc-token-icon"
                        src={position.icon}
                        alt=""
                      />
                    )}
                    <span>{position.tokenSymbol}</span>
                  </div>
                </div>
                <div className="wp-item-line">
                  <span className="wp-item-label">Deposited</span>
                  <div className="wp-svc-token wp-svc-token--amount">
                    {position.icon && (
                      <img
                        className="wp-svc-token-icon"
                        src={position.icon}
                        alt=""
                      />
                    )}
                    <div className="wp-svc-amount">
                      <span className="wp-svc-amount-main">
                        {formatPortfolioTokenAmount(
                          position.depositedRaw,
                          position.decimals,
                        )}
                      </span>
                      {depositedUsd && (
                        <span className="wp-item-usd">{depositedUsd}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="wp-item-tags">
                {flow.canWithdrawInline ? (
                  <button
                    type="button"
                    className="wp-tag wp-tag--muted"
                    data-track="points_withdraw_open"
                    onClick={() => flow.openWithdraw(position.poolAddress)}
                  >
                    <span className="wp-tag-label">Withdraw</span>
                  </button>
                ) : (
                  <a
                    className="wp-tag wp-tag--muted"
                    href={pointsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-track="points_withdraw_open"
                    onClick={(e) =>
                      handleOutlinkClick(
                        e,
                        pointsUrl,
                        {
                          category: "portfolio",
                          origin: "portfolio-points",
                          payload: { position },
                        },
                        onOutlink,
                      )
                    }
                  >
                    <span className="wp-tag-label">Withdraw</span>
                    <ExternalLinkIcon size={12} className="wp-tag-icon" />
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </SectionCard>

      <WithdrawDialog
        open={flow.isOpen}
        status={flow.status}
        positions={pointPositions}
        initialPoolAddress={flow.initialPoolAddress}
        walletAddress={walletAddress}
        txHash={flow.txHash}
        error={flow.error}
        copied={flow.copied}
        estimateWithdraw={flow.hasEstimator ? flow.estimateWithdraw : undefined}
        onClose={flow.closeWithdraw}
        onConfirm={flow.confirmWithdraw}
        onCopyHash={flow.copyHash}
        trackFunnel={flow.trackFunnelOnce}
        nativeSymbol={getNativeToken()?.symbol}
      />
    </>
  );
}
